import { ChartPoint } from "../chart/ChartPoint";
import { Milestone } from "../projects/Milestone";

export interface MetricSnapshot {
  date: string;
  value: number;
}

// "positive" = increase is good, "negative" = decrease is good, "neutral" = no judgment
export type DeltaPolarity = "positive" | "negative" | "neutral";

export interface MetricComparison {
  label: string;
  borderColor: string;
  bgColor: string;
  snapshotA: MetricSnapshot | null;
  snapshotB: MetricSnapshot | null;
  delta: number | null;
  deltaPercent: number | null;
  isCount: boolean;
  polarity: DeltaPolarity;
}

export interface SnapshotComparisonResult {
  dateA: string;
  dateB: string;
  metrics: MetricComparison[];
}

/**
 * Find the data point whose date is the latest date <= targetDate.
 * Points are assumed sorted ascending by date.
 */
function toDateOnly(date: string): string {
  return new Date(date).toISOString().split("T")[0];
}

function findSnapshotAtDate(
  points: ChartPoint[],
  targetDate: string
): MetricSnapshot | null {
  const target = toDateOnly(targetDate);
  let best: ChartPoint | null = null;
  for (const point of points) {
    const pointDay = toDateOnly(point.date);
    if (pointDay <= target) {
      best = point;
    } else {
      break;
    }
  }
  return best ? { date: best.date, value: best.value } : null;
}

function compareMetric(
  label: string,
  borderColor: string,
  bgColor: string,
  points: ChartPoint[],
  dateA: string,
  dateB: string,
  isCount: boolean,
  polarity: DeltaPolarity
): MetricComparison {
  const snapshotA = findSnapshotAtDate(points, dateA);
  const snapshotB = findSnapshotAtDate(points, dateB);
  let delta: number | null = null;
  let deltaPercent: number | null = null;
  if (snapshotA && snapshotB) {
    delta = snapshotB.value - snapshotA.value;
    deltaPercent = snapshotA.value !== 0 ? (delta / snapshotA.value) * 100 : null;
  }
  return { label, borderColor, bgColor, snapshotA, snapshotB, delta, deltaPercent, isCount, polarity };
}

const METRIC_DEFS: {
  label: string;
  borderColor: string;
  bgColor: string;
  field: keyof Milestone;
  isCount: boolean;
  polarity: DeltaPolarity;
}[] = [
  {
    label: "Total Estimate",
    borderColor: "rgba(75, 192, 192, 1)",
    bgColor: "rgba(75, 192, 192, 0.05)",
    field: "totalEstimate",
    isCount: false,
    polarity: "neutral",
  },
  {
    label: "Actual Burnup",
    borderColor: "rgba(255, 99, 132, 1)",
    bgColor: "rgba(255, 99, 132, 0.05)",
    field: "actualBurnUp",
    isCount: false,
    polarity: "positive",
  },
  {
    label: "Estimate Burnup",
    borderColor: "rgba(255, 159, 64, 1)",
    bgColor: "rgba(255, 159, 64, 0.05)",
    field: "estimateBurnUp",
    isCount: false,
    polarity: "positive",
  },
  {
    label: "Est. Total Hours",
    borderColor: "rgba(153, 102, 255, 1)",
    bgColor: "rgba(153, 102, 255, 0.05)",
    field: "estimatedTotalHoursNeeded",
    isCount: false,
    polarity: "negative",
  },
  {
    label: "Total Task Estimate",
    borderColor: "rgba(54, 162, 235, 1)",
    bgColor: "rgba(54, 162, 235, 0.05)",
    field: "totalTaskEstimates",
    isCount: false,
    polarity: "neutral",
  },
  {
    label: "Total Of Est. Completed",
    borderColor: "rgba(255, 105, 180, 1)",
    bgColor: "rgba(255, 105, 180, 0.05)",
    field: "totalOfEstimatedCompleted",
    isCount: false,
    polarity: "positive",
  },
  {
    label: "Total Tasks",
    borderColor: "rgba(201, 203, 207, 1)",
    bgColor: "rgba(201, 203, 207, 0.05)",
    field: "totalTasks",
    isCount: true,
    polarity: "neutral",
  },
];

export function computeSnapshotComparison(
  milestone: Milestone,
  dateA: string,
  dateB: string
): SnapshotComparisonResult {
  const metrics = METRIC_DEFS.map((def) =>
    compareMetric(
      def.label,
      def.borderColor,
      def.bgColor,
      milestone[def.field] as ChartPoint[],
      dateA,
      dateB,
      def.isCount,
      def.polarity
    )
  );
  return { dateA, dateB, metrics };
}

export function getMilestoneDateRange(
  milestone: Milestone
): { min: string; max: string } | null {
  const allPoints: ChartPoint[] = METRIC_DEFS.flatMap(
    (def) => milestone[def.field] as ChartPoint[]
  );
  if (allPoints.length === 0) return null;
  const timestamps = allPoints.map((p) => new Date(p.date).getTime());
  const minTime = Math.min(...timestamps);
  const maxTime = Math.max(...timestamps);
  return {
    min: new Date(minTime).toISOString().split("T")[0],
    max: new Date(maxTime).toISOString().split("T")[0],
  };
}
